import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { SafeImage } from "./SafeImage";
import { fetchRelatedBoxArticles } from "@/lib/related-box-articles.functions";

interface Props {
  boxSlug: string;
  currentArticleSlug: string;
}

export function RelatedBoxArticles({ boxSlug, currentArticleSlug }: Props) {
  const fetchRelated = useServerFn(fetchRelatedBoxArticles);
  
  const { data: articles, isLoading } = useQuery({
    queryKey: ["related-box-articles", boxSlug, currentArticleSlug],
    queryFn: () => fetchRelated({ data: { boxSlug, currentArticleSlug } }),
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
    refetchOnWindowFocus: false,
  });

  if (isLoading || !articles || articles.length === 0) return null;

  return (
    <section className="mt-16 border-t border-border pt-12">
      <div className="flex items-center justify-between mb-8">
        <h2 className="font-display text-2xl font-bold text-foreground sm:text-3xl">
          Related Articles
        </h2>
        <span className="text-xs font-semibold uppercase tracking-[0.2em] text-accent">
          Keep Reading
        </span>
      </div>
      
      <div className="grid gap-6 sm:grid-cols-3">
        {articles.map((article) => (
          <Link
            key={article.slug}
            to="/topics/$slug/$article"
            params={{ slug: article.box_slug, article: article.slug }}
            title={`Read: ${article.title}`}
            className="group flex flex-col rounded-2xl border border-border bg-card overflow-hidden shadow-sm transition-all hover:-translate-y-1 hover:border-accent hover:shadow-lg hover:shadow-accent/5"
          >
            <div className="relative h-48 w-full overflow-hidden border-b border-border">
              <SafeImage
                src={article.hero_image_url || "/placeholder.jpg"}
                alt={article.title}
                width={640}
                height={360}
                loading="lazy"
                decoding="async"
                fetchPriority="low"
                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
              />
            </div>
            <div className="flex flex-1 flex-col p-5">
              <h3 className="font-display text-lg font-bold text-foreground group-hover:text-accent transition-colors">
                {article.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-muted-foreground line-clamp-3">
                {article.excerpt}
              </p>
              <div className="mt-auto pt-4 flex items-center text-xs font-semibold text-accent group-hover:underline">
                Read guide →
              </div>
            </div>
          </Link>
        ))}
      </div>
    </section>
  );
}
